import React, { useEffect, JSX } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { usePermissions } from "components/Hook";
import PageLoader from "components/common/PageLoader";
import EditSalary from "components/Screens/Workforce/UserDetailsV2/EditSalary";
import { getFieldValue, isEmptyObject, isValidResponse } from "components/Screens/CommonUtils";

import { employeeSalaryDetails } from "store/actions/Workforce/EmployeeDetils/SalaryDetails";
import { employeeSalaryInfo } from "store/actions/Workforce/EmployeeDetils/SalaryInfo";
import { GetEmployeeDetails } from "store/actions/GetEmployeeDetails";
import { EmployeeInfo } from "store/actions/EmployeeInfo";

import NavigationRoutes from "constants/NavigationRoutes";

interface LoggedInUser {
  customerId?: string;
  [key: string]: any;
}

interface IncrementDetails {
  bonus?: number;
  incrementAmount?: number;
  [key: string]: any;
}

interface EmployeeDetailsPayload {
  employeeId?: string;
  incrementDetails?: IncrementDetails;
  costToCompany?: number;
  [key: string]: any;
}

interface EmployeeDetailsState {
  payload?: EmployeeDetailsPayload;
  [key: string]: any;
}

interface RootState {
  GetEmployeeDetails: {
    getEmployeeDetails: EmployeeDetailsState;
    loadingGET_EMPLOYEE_Details: boolean;
  };
  SalaryInfo: {
    loadingEmployeeSalaryInfo: boolean;
  };
}

function EditSalaryRoute(): JSX.Element {
  const { id: employeeId = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedInUser, loggedInUserType } = usePermissions() as {
    loggedInUser: LoggedInUser;
    loggedInUserType: string;
  };

  const getEmployeeDetails = useSelector((state: RootState) => state.GetEmployeeDetails.getEmployeeDetails);
  const loadingEmployeeSalaryInfo = useSelector((state: RootState) => state.SalaryInfo.loadingEmployeeSalaryInfo);
  const loadingGetEmployeeDetails = useSelector(
    (state: RootState) => state.GetEmployeeDetails.loadingGET_EMPLOYEE_Details
  );

  const { incrementDetails = {}, costToCompany = 0 } = getEmployeeDetails?.payload || {};
  const { bonus = 0, incrementAmount = 0 } = incrementDetails || {};
  const { customerId = "" } = loggedInUser || {};

  useEffect(() => {
    if (isEmptyObject(loggedInUser) && customerId && loggedInUserType === "customer") {
      dispatch(
        EmployeeInfo({
          customerID: customerId,
          rowsPerPage: 1000,
          page: 0,
          sort: {
            field: "employeeId",
            direction: "DESC",
          },
        })
      );
    }
  }, [dispatch, loggedInUser, loggedInUserType]);

  useEffect(() => {
    if (isEmptyObject(loggedInUser) && employeeId) {
      const request: Record<string, string> = {
        customer: `customer/${customerId}/employee/${employeeId}`,
        edge: `employee/${employeeId}`,
      };

      dispatch(
        GetEmployeeDetails({
          request: request[loggedInUserType as keyof typeof request],
        })
      );
    }
  }, [loggedInUser, employeeId, loggedInUserType, dispatch]);

  useEffect(() => {
    if (isEmptyObject(loggedInUser) && isValidResponse(getEmployeeDetails)) {
      const salaryInfoRequest: Record<string, string> = {
        customer: `customer/${customerId}/employee/${employeeId}/salaryInfo`,
        edge: `employee/${employeeId}/salary`,
      };

      const salaryDetailsRequest: Record<string, string> = {
        customer: `customer/${customerId}/employee/${employeeId}/payroll`,
        edge: `employee/${employeeId}/payroll`,
      };

      dispatch(
        employeeSalaryDetails({
          request: salaryDetailsRequest[loggedInUserType as keyof typeof salaryDetailsRequest],
        })
      );

      dispatch(
        employeeSalaryInfo({
          request: salaryInfoRequest[loggedInUserType as keyof typeof salaryInfoRequest],
        })
      );
    }
  }, [getEmployeeDetails, loggedInUser, loggedInUserType, dispatch]);

  const handleBackToProfile = (): void => {
    navigate(NavigationRoutes.workforceDetails.replace(":id", employeeId));
  };

  const calculatedCostToCompany: number | string = costToCompany
    ? getFieldValue(costToCompany) - getFieldValue(bonus) - getFieldValue(incrementAmount)
    : "-";

  if (loadingGetEmployeeDetails || loadingEmployeeSalaryInfo) {
    return <PageLoader />;
  }

  return <EditSalary setTab={handleBackToProfile} costToCompany={calculatedCostToCompany} />;
}

export default EditSalaryRoute;
